import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import type { ObjectShare, ShareCounterparties } from '../../types/api';

// STD-015: server state for the file/folder-sharing domain (#1182 Stage 4
// slice 4). apiService is imported here (a *.queries.ts file) per STD-016.

export interface ObjectShareListFilters {
  objectId?: string;
  includeRevoked?: boolean;
}

// A namespace of its own rather than a branch under `flight-shares`: granting a
// file share must not invalidate the flight lists, and vice versa. The two
// surfaces are independent even though their shapes rhyme.
export const objectSharesKeys = {
  all: ['object-shares'] as const,
  list: (filters: ObjectShareListFilters) => [...objectSharesKeys.all, 'list', filters] as const,
  counterparties: () => [...objectSharesKeys.all, 'counterparties'] as const,
};

/**
 * Shares the caller's org granted or received on files and folders.
 *
 * `enabled` is a parameter rather than derived from the filters, matching the
 * flight query: an unfiltered list is legitimate, so "no filters" must not mean
 * "don't run". The dialog passes `open`.
 */
export function useObjectSharesQuery(filters: ObjectShareListFilters, enabled = true) {
  return useQuery<ObjectShare[], Error>({
    queryKey: objectSharesKeys.list(filters),
    enabled,
    // The dialog renders failures inline and keeps them on screen next to the
    // control that caused them. Leaving the global toast on would report the
    // same failure twice.
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => {
      const response = await apiService.listObjectShares(filters);
      return response.shares;
    },
  });
}

/**
 * The organizations this caller may legally share files with, plus its own org id.
 *
 * Separate cache key from the flight counterparties even though the payload is
 * identical today: they are different endpoints, and sharing one cache entry
 * would mean a change to either backend's rule silently propagating to the
 * other surface's picker.
 *
 * `staleTime` is generous because the answer is the hub-and-spoke topology,
 * which changes only when an organization is created — refetching it on every
 * dialog open would be a request per right-click. The global error toast is
 * suppressed because this query is admin-gated (`require_org_admin`), so a
 * non-admin should see the dialog degrade, not a toast blaming them.
 */
export function useObjectShareCounterpartiesQuery(enabled = true) {
  return useQuery<ShareCounterparties, Error>({
    queryKey: objectSharesKeys.counterparties(),
    enabled,
    staleTime: 5 * 60 * 1000,
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => apiService.getObjectShareCounterparties(),
  });
}
